import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';

/**
 * Data structure for interacted controls with timestamps
 * Maps controlId -> ISO timestamp string of when the interaction occurred
 */
export type InteractedControlsMap = Record<string, string>;

/**
 * Hook to track which controls the user has interacted with
 * Persists to Firebase at users/{uid}.interactedControls (legacy array) and
 * users/{uid}.interactedControlsWithTimestamps (new map with timestamps)
 * 
 * A control is considered "interacted" when the user:
 * - Meaningfully changes its value (slider moved, button toggled)
 * - Clicks on the help (?) icon for that control
 * 
 * The hook maintains backward compatibility by reading from both the old
 * array format and the new timestamp format, merging them together.
 */
export function useInteractedControls() {
  const { user } = useAuth();
  const [interactedControls, setInteractedControls] = useState<Set<string>>(new Set());
  const [interactedControlsWithTimestamps, setInteractedControlsWithTimestamps] = useState<InteractedControlsMap>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load interacted controls from Firebase on mount/user change
  useEffect(() => {
    if (!user) {
      setInteractedControls(new Set());
      setInteractedControlsWithTimestamps({});
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const loadInteractedControls = async () => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (mounted && userDoc.exists()) {
          const data = userDoc.data();
          
          // Load new format with timestamps
          const withTimestamps = data?.interactedControlsWithTimestamps as InteractedControlsMap || {};
          
          // Load legacy format (array of strings) for backward compatibility
          const legacyControls = data?.interactedControls as string[] || [];
          
          // Merge: legacy controls without timestamps get a placeholder
          // New controls with timestamps take precedence
          const mergedMap: InteractedControlsMap = { ...withTimestamps };
          for (const controlId of legacyControls) {
            if (!mergedMap[controlId]) {
              // Legacy control without timestamp - use empty string as placeholder
              mergedMap[controlId] = '';
            }
          }
          
          setInteractedControlsWithTimestamps(mergedMap);
          setInteractedControls(new Set(Object.keys(mergedMap)));
        }
      } catch (error) {
        console.error('Failed to load interacted controls:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadInteractedControls();

    return () => {
      mounted = false;
    };
  }, [user]);

  /**
   * Mark a control as interacted with timestamp
   * @param controlId The lowercase control identifier (e.g., "prioritize", "google")
   */
  const markInteracted = useCallback(async (controlId: string) => {
    const normalizedId = controlId.toLowerCase().trim();
    
    // Skip if already interacted
    if (interactedControls.has(normalizedId)) {
      return;
    }

    const timestamp = new Date().toISOString();

    // Update local state immediately for responsive UI
    setInteractedControls(prev => {
      const next = new Set(prev);
      next.add(normalizedId);
      return next;
    });
    
    setInteractedControlsWithTimestamps(prev => ({
      ...prev,
      [normalizedId]: timestamp
    }));

    // Persist to Firebase
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const existingWithTimestamps = userDoc.exists() 
          ? (userDoc.data()?.interactedControlsWithTimestamps as InteractedControlsMap || {})
          : {};
        
        // Only add if not already tracked
        if (!existingWithTimestamps[normalizedId]) {
          await setDoc(userRef, {
            interactedControlsWithTimestamps: {
              ...existingWithTimestamps,
              [normalizedId]: timestamp
            }
          }, { merge: true });
          console.log(`✅ Marked control as interacted: ${normalizedId} at ${timestamp}`);
        }
      } catch (error) {
        console.error('Failed to persist interacted control:', error);
      }
    }
  }, [user, interactedControls]);

  /**
   * Check if a control has been interacted with
   * @param controlId The control identifier to check
   */
  const hasInteracted = useCallback((controlId: string): boolean => {
    const normalizedId = controlId.toLowerCase().trim();
    return interactedControls.has(normalizedId);
  }, [interactedControls]);

  /**
   * Get the timestamp when a control was interacted with
   * @param controlId The control identifier to check
   * @returns ISO timestamp string, or empty string if unknown, or undefined if not interacted
   */
  const getInteractionTimestamp = useCallback((controlId: string): string | undefined => {
    const normalizedId = controlId.toLowerCase().trim();
    return interactedControlsWithTimestamps[normalizedId];
  }, [interactedControlsWithTimestamps]);

  /**
   * Check if the help icon should be shown for a control
   * Returns true if the control has help configured AND user hasn't interacted with it
   */
  const shouldShowHelp = useCallback((controlId: string, hasHelpConfigured: boolean): boolean => {
    if (!hasHelpConfigured) return false;
    return !hasInteracted(controlId);
  }, [hasInteracted]);

  return {
    interactedControls,
    interactedControlsWithTimestamps,
    markInteracted,
    hasInteracted,
    getInteractionTimestamp,
    shouldShowHelp,
    isLoading
  };
}


